import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import {
  CloudUpload,
  CheckCircle,
  Error,
  ArrowBack,
  Description,
  TableChart,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import api from "../../utils/api";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ImportProspects = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
        "application/csv",
      ];

      if (allowedTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
        setError(null);
        setResult(null);
      } else {
        setError("Please select a valid Excel (.xlsx, .xls) or CSV file");
        setFile(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.prospects.import(formData);

      setResult(response);
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById("file-upload");
      if (fileInput) {
        fileInput.value = "";
      }
    } catch (err) {
      console.error("Import error:", err);
      setError(err.response?.data?.error || err.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/prospects");
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ mb: 2 }}>
          Back to Prospects
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Import Prospects
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload an Excel or CSV file to import prospects into your database.
        </Typography>
      </Box>

      {/* Instructions Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            File Format Requirements
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            For Excel files, we will process data from the following sheets:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip
              icon={<TableChart />}
              label="Portal QBs"
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<TableChart />}
              label="Portal RBs"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Supported columns include: First Name, Last Name, Position, Email,
            Phone, Height, Weight, Graduation Year, High School, City, State,
            GPA, SAT, ACT, 40 Yard, Wingspan, Vertical Jump, and social media
            links.
          </Typography>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            startIcon={<CloudUpload />}
            size="large"
            sx={{ minWidth: 200 }}
          >
            Select File
            <VisuallyHiddenInput
              id="file-upload"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
            />
          </Button>

          {file && (
            <Box sx={{ textAlign: "center" }}>
              <Chip
                icon={<Description />}
                label={file.name}
                color="primary"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                File size: {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!file || loading}
            size="large"
            sx={{ minWidth: 200 }}
          >
            {loading ? "Importing..." : "Import Prospects"}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, textAlign: "center" }}
            >
              Processing file and importing prospects...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Success Result */}
      {result && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CheckCircle color="success" />
            <Typography variant="h6" color="success.main">
              Import Completed Successfully!
            </Typography>
          </Box>

          <Typography variant="body1" gutterBottom>
            {result.message}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, my: 2 }}>
            <Chip
              label={`${result.importedCount} Imported`}
              color="success"
              variant="outlined"
            />
            {result.errors && result.errors.length > 0 && (
              <Chip
                label={`${result.errors.length} Warnings`}
                color="warning"
                variant="outlined"
              />
            )}
          </Box>

          {result.errors && result.errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                Warnings:
              </Typography>
              <List dense>
                {result.errors.slice(0, 10).map((error, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={error}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "text.secondary",
                      }}
                    />
                  </ListItem>
                ))}
                {result.errors.length > 10 && (
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={`... and ${
                        result.errors.length - 10
                      } more warnings`}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button variant="contained" onClick={handleGoBack} color="primary">
              View Prospects
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setResult(null);
                setError(null);
              }}
            >
              Import Another File
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default ImportProspects;
